<template>
  <BaseDialog
    v-model="isActive"
    :title="title"
  >
    <v-sheet class="h-100 px-2 pt-4 pa-md-6 d-flex flex-column justify-space-between">
      <v-form
        ref="connectMedtochkaForm"
        v-model="isFormValid"
      >
        <div class="text-body-1 mb-6">
          {{ subtitle }}
        </div>
        <v-text-field
          v-model.trim="name"
          class="mb-2"
          label="Ваше имя"
          variant="outlined"
          :rules="nameRules"
          :density="$display.smAndDown.value ? 'default' : 'compact'"
        />
        <PhoneInput
          class="mb-3"
          label="Номер телефона"
          variant="outlined"
          :value="phone"
          :density="$display.smAndDown.value ? 'default' : 'compact'"
          @update:value="phone = $event"
        />
      </v-form>

      <div class="d-flex flex-column align-center text-center d-md-block text-md-start">
        <div class="text-body-2 text-ui-kit-text-secondary mb-4">
          Нажимая на кнопку «Жду звонка», вы даёте согласие на
          <NuxtLink
            target="_blank"
            class="text-ui-kit-text-secondary"
            :to="withRouteParams({ name: 'privacy-policy' })"
          >
            обработку персональных данных
          </NuxtLink>
        </div>
        <v-sheet
          width="100%"
          class="d-flex justify-end"
        >
          <BaseMtButton
            color="primary mb-2 mb-md-0"
            :block="$display.smAndDown.value"
            :disabled="!isPhoneCorrect || !name || !isFormValid"
            @click="handleSendRequest"
          >
            Жду звонка
          </BaseMtButton>
        </v-sheet>
      </div>
    </v-sheet>
  </BaseDialog>
</template>

<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { getOnlyNumber } from '~/utils/getOnlyNumber'
import { PHONE_LENGTH } from '~/constants/phone'
import { ConnectionRequest } from '~/features/Home/enums'
import { CONNECTION_FORM_LIMIT } from '~/features/Home/constants'
import { HomeApi } from '~/features/Home/api/homeApi'

const sentRequests = {
  [ConnectionRequest.Medtochka]: 0,
  [ConnectionRequest.Telemed]: 0,
}

const nameRules = [
  (v: string) => v === null || !v || (/^[\u0410-\u042F\u0430-\u044F\u0401\u0451\u00EB\u00CB\- ]+$/).test(v.trim()) || !v.trim() || 'Используйте только кириллицу',
  (v: string) => v === null || v.trim().length <= 100 || 'Должно быть не больше 100 символов',
]

const emit = defineEmits(['click:send', 'error'])

const connectMedtochkaForm = ref<{ validate: () => void } | null>(null)

const isActive = ref(false)
const isFormValid = ref(false)
const request = ref<ConnectionRequest | null>(null)
const name = ref('')
const phone = ref('')

const $display = useDisplay()

const title = computed(() => {
  switch (request.value) {
    case ConnectionRequest.Medtochka:
      return 'Подключить МедТочку'
    case ConnectionRequest.Telemed:
      return $display.mdAndUp.value ? 'Подключить Телемедицину МедТочка' : 'Телемедицина МедТочка'
  }
})

const subtitle = computed(() => {
  switch (request.value) {
    case ConnectionRequest.Medtochka:
      return 'Оставьте контакты — мы перезвоним и расскажем, как подключить МедТочку'
    case ConnectionRequest.Telemed:
      return 'Оставьте контакты — мы перезвоним и расскажем, как подключить Телемедицину МедТочка. Подключение сервиса доступно только для клиник.'
  }
})

const isPhoneCorrect = computed(() => getOnlyNumber(phone.value).length === PHONE_LENGTH)

function open(requestPayload: ConnectionRequest) {
  request.value = requestPayload
  name.value = ''
  phone.value = ''
  isActive.value = true
}

function hide() {
  isActive.value = false
}

async function handleSendRequest() {
  await connectMedtochkaForm.value?.validate()

  if (!isFormValid.value) {
    return
  }

  if (!request.value || sentRequests[request.value] >= CONNECTION_FORM_LIMIT) {
    hide()

    return
  }

  try {
    sentRequests[request.value]++

    await HomeApi.sendFeedback({
      form_type: request.value,
      name: name.value,
      phone: getOnlyNumber(phone.value),
    })

    emit('click:send')
  } catch {
    emit('error')
  } finally {
    hide()
  }
}

defineExpose({
  open,
  hide,
})
</script>
